import { EditOutlined } from '@ant-design/icons';
import { Button, Table, Tag } from 'antd';
import React from 'react';
import { AutoSizer } from 'react-virtualized';
import { withTranslation, WithTranslation } from 'react-i18next';
import * as utils from '../../../utils/Utils';
import { MultiAccountUser, AdminAccount } from '../../../../types/user';

type MultiAccountUserTableProps = {
  userList: MultiAccountUser[];
  adminAccounts: AdminAccount[];
  filterKeywords: string;
  editUser: (userId: number) => void;
} & WithTranslation;

class MultiAccountUserTable extends React.Component<MultiAccountUserTableProps> {
  filterUsers = () => {
    const { userList, filterKeywords } = this.props;
    const keywords = utils.stringToArray(filterKeywords.toLowerCase());

    return userList.filter(user => {
      const firstName = user.first_name.toLowerCase();
      const lastName = user.last_name.toLowerCase();
      const email = user.email.toLowerCase();
      return keywords.every(
        keyword =>
          firstName.includes(keyword) || lastName.includes(keyword) || email.includes(keyword)
      );
    });
  };

  getAccountTags = (user: MultiAccountUser) => {
    const { adminAccounts } = this.props;
    return user.account_assignments.map(assignment => {
      const account = adminAccounts.find(acc => acc.account_id === assignment.account_id);
      if (!account) return null;

      return (
        <Tag key={assignment.account_id} color={assignment.admin ? 'blue' : 'default'}>
          {account.account_name}
          {assignment.admin === 1 && ' (Admin)'}
        </Tag>
      );
    });
  };

  createTableRow = (user: MultiAccountUser) => {
    const { t } = this.props;

    return {
      key: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      accounts: this.getAccountTags(user),
      status: user.active ? t('user:active') : t('user:inactive'),
      actions: (
        <Button icon={<EditOutlined />} onClick={() => this.props.editUser(user.id)}>
          {t('user:edit')}
        </Button>
      ),
    };
  };

  render() {
    const { userList, filterKeywords, t } = this.props;

    const columns = [
      { title: t('user:firstName'), dataIndex: 'firstName', width: '150px' },
      { title: t('user:lastName'), dataIndex: 'lastName', width: '150px' },
      { title: t('user:email'), dataIndex: 'email', width: '200px' },
      { title: t('user:accounts'), dataIndex: 'accounts', width: '300px' },
      { title: t('user:status'), dataIndex: 'status', width: '130px' },
      { dataIndex: 'actions', hideHeader: true, width: '100px' },
    ];

    const users = filterKeywords ? this.filterUsers() : userList;

    const dataSource = users.map(user => {
      return this.createTableRow(user);
    });

    // Row height is ~57px, header is ~47px
    const tableContentHeight = users.length * 57 + 47;

    return (
      <div className="user-content__wrapper">
        <AutoSizer>
          {({ height, width }) => {
            const needsScroll = tableContentHeight > height;
            return (
              <div style={{ height, width }}>
                <Table
                  className="user-table"
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  size="middle"
                  scroll={{
                    x: width > 1030 ? undefined : 1030,
                    y: needsScroll ? height - 47 : undefined,
                  }}
                />
              </div>
            );
          }}
        </AutoSizer>
      </div>
    );
  }
}

export default withTranslation()(MultiAccountUserTable);
